import { BRAND_GRADIENT } from '@shared/styles/brand';
import { useBuyCredits, useCreditPack } from './api';
import { featureBacktesting } from '@config/env';

/**
 * The balance the user held the instant before they left for Stripe Checkout. `CreditsSuccessPage`
 * reads it on the way back to tell "the webhook has landed" (balance went UP) from "still waiting".
 * sessionStorage, not localStorage: it is meaningful for exactly one checkout round-trip in one tab.
 */
export const CREDITS_BEFORE_PURCHASE_KEY = 'journal.credits.beforePurchase';

/**
 * `1499` + `"usd"` → `"$14.99"`. Never a hard-coded dollar string — the pack's price and currency
 * both come from server config, and a EUR-priced pack must not render as dollars. Falls back to a
 * plain `14.99 USD` if the configured currency code is one `Intl` refuses (a config typo must not
 * throw inside a render and take the whole composer panel down with it).
 */
function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/** sessionStorage can throw (Safari private mode) — a failed marker must not block the purchase. */
function rememberBalance(balance: number | undefined): void {
  try {
    window.sessionStorage.setItem(CREDITS_BEFORE_PURCHASE_KEY, String(balance ?? ''));
  } catch {
    // Ignored: without the marker the success page simply falls back to its poll budget.
  }
}

/**
 * "Buy 100 credits — $14.99" (2026-08-14). A one-off Stripe payment that tops up the AI-backtest
 * credit balance — deliberately NOT tier-gated, because the user who needs it most is a free-tier
 * trader who has run out.
 *
 * Renders NOTHING when the pack query errored (a 404 = `Stripe:Enabled` is off) or when the server
 * reports `available: false` (no `Stripe:CreditPack:PriceId` configured). A dead button that 409s
 * on click would be worse than no button at all, so the feature simply isn't there when it's dark.
 */
/**
 * At or below this balance the CTA shouts — the full "Buy N credits — $X" gradient pill. Above it,
 * the same action is a quiet "Buy more" link (2026-08-15 user request). A user sitting on 253
 * credits does not need a purchase button competing with the thing they are actually doing; a user
 * down to their last few does.
 */
export const LOW_CREDITS_THRESHOLD = 10;

/**
 * At or above this balance the CTA is not rendered at all (2026-08-30 user request: "put the buy more
 * button only when there are less than 20 credits"). Someone sitting on 530 credits has no use for a
 * purchase button; below 20 it starts earning its place, quietly at first and loudly under
 * {@link LOW_CREDITS_THRESHOLD}.
 *
 * `emphasis="full"` is exempt — that is the pricing page's "Need more credits?" panel, whose entire
 * purpose IS the purchase, and hiding the only action on a panel dedicated to it would break it.
 */
export const HIDE_CREDITS_THRESHOLD = 20;

export function BuyCreditsButton({ balance, emphasis = 'auto' }: {
  /** The caller's CURRENT credit balance — recorded before the redirect so the success page can
   *  detect the top-up. `undefined` while the credits query is still loading. */
  balance: number | undefined;
  /**
   * `auto` (default) lets the balance choose the volume — quiet link above the threshold, full pill
   * at or below it. `full` pins the loud form for surfaces whose entire purpose IS the purchase
   * (the pricing page's "Need more credits?" panel), where a quiet link would bury the only action
   * on the panel.
   */
  emphasis?: 'auto' | 'full';
}) {
  const pack = useCreditPack();
  const buy = useBuyCredits();

  // AI credits exist only to be spent in Backtesting AI, so the purchase follows that feature's flag
  // (2026-08-17). Checked alongside the existing `available` guard rather than before the hooks: the
  // hook order must stay unconditional, and this component already renders nothing when the pack is
  // unavailable, so "off" is a case the surrounding UI is built to absorb.
  if (!featureBacktesting || pack.isError || !pack.data?.available) return null;

  const { credits, amountCents, currency } = pack.data;

  // Plenty of credits ⇒ no button at all. Only a KNOWN balance hides it: while the credits query is
  // still loading the balance is `undefined`, and treating that as "flush" would hide the CTA from
  // the very users who need it every time the query refetches.
  if (emphasis !== 'full' && balance !== undefined && balance >= HIDE_CREDITS_THRESHOLD) return null;

  // `undefined` = the balance hasn't loaded. Stay QUIET then: guessing "low" would flash a full
  // gradient CTA at users who are flush, every time the credits query refetches.
  const low = emphasis === 'full' || (balance !== undefined && balance <= LOW_CREDITS_THRESHOLD);

  const start = () => {
    rememberBalance(balance);
    buy.mutate();
  };

  // The quiet form. Same action, same disabled/redirecting handling — only the volume differs.
  if (!low) {
    return (
      <button
        type="button"
        disabled={buy.isPending}
        onClick={start}
        className="shrink-0 whitespace-nowrap px-1 text-[10px] font-semibold uppercase tracking-wide transition-opacity hover:opacity-70 disabled:cursor-default disabled:opacity-60"
        style={{ color: 'var(--brand-dark)', lineHeight: '20px' }}
      >
        {buy.isPending ? 'Redirecting…' : 'Buy more'}
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={buy.isPending}
      onClick={start}
      className="shrink-0 whitespace-nowrap rounded-full px-3 text-[10px] font-semibold text-white transition-opacity hover:opacity-85 disabled:cursor-default disabled:opacity-60"
      style={{ background: BRAND_GRADIENT, lineHeight: '20px' }}
    >
      {buy.isPending ? 'Redirecting…' : `Buy ${credits} credits (${formatPrice(amountCents, currency)})`}
    </button>
  );
}
